import React, { useMemo } from 'react';
import { format, differenceInDays, startOfYear, endOfYear } from 'date-fns';
import { fr } from 'date-fns/locale';
import SortableTable from '../SortableTable';
import AnnualProgressBar from '../ui/AnnualProgressBar';
import useVisitsTargetsAnalysis from '../../hooks/useVisitsTargetsAnalysis';
import useAnnualProgress from '../../hooks/useAnnualProgress';
import { obfuscateUsername } from '../../pages/VisitsViewer';
import { isCompletedVisit, VISIT_STATUS } from '../../constants/visitsClassification';
import { countStoreVisits } from '../../constants/visitsUnivers';

function VisitsPlanned({
  visits,
  startDate,
  endDate,
  selectedProject,
  selectedShopSigns,
  fetchWithAuth,
  obfuscate
}) {
  // Utiliser le hook d'analyse des objectifs
  const { getStoreTargetStatus, getStoreTargetStatusAfterVisit, filteredTargets, periodDays, loading } = useVisitsTargetsAnalysis({
    visits,
    startDate,
    endDate,
    selectedProject,
    selectedShopSigns,
    fetchWithAuth
  });

  // Utiliser le nouveau hook pour l'avancement annuel
  const { annualProgressData, loading: annualProgressLoading } = useAnnualProgress({
    selectedProject,
    startDate,
    endDate,
    fetchWithAuth
  });

  const plannedVisits = useMemo(() => {
    const now = new Date();
    return (visits || []).filter(visit => {
      const isPlannedStatus = visit.status === VISIT_STATUS.PLANNED;
      const hasPlannedDate = visit.plannedStart instanceof Date;
      const isInFuture = hasPlannedDate && visit.plannedStart > now;
      return isPlannedStatus && isInFuture;
    });
  }, [visits]);

  const formatDateTime = (date) => {
    if (!date) return '—';
    return format(date, 'dd/MM/yyyy HH:mm', { locale: fr });
  };

  // Fonction pour obtenir le statut d'objectif d'une visite
  const getVisitTargetStatus = (visit) => {
    if (!visit.interventionPoint) {
      return {
        status: 'no-data',
        message: 'Magasin non défini',
        color: 'text-gray-500'
      };
    }

    const targetStatus = getStoreTargetStatus(visit.interventionPoint);

    let color;
    switch (targetStatus.status) {
      case 'under-target':
        color = 'text-red-600 bg-red-50';
        break;
      case 'over-target':
        color = 'text-orange-600 bg-orange-50';
        break;
      case 'on-target':
        color = 'text-green-600 bg-green-50';
        break;
      case 'no-target':
        color = 'text-yellow-600 bg-yellow-50';
        break;
      default:
        color = 'text-gray-500 bg-gray-50';
    }

    return {
      ...targetStatus,
      color
    };
  };

  // Fonction pour obtenir le statut d'objectif APRÈS la visite planifiée
  const getVisitTargetStatusAfterPlanned = (visit) => {
    if (!visit.interventionPoint) {
      return {
        status: 'no-data',
        message: 'Magasin non défini',
        color: 'text-gray-500'
      };
    }

    const targetStatus = getStoreTargetStatusAfterVisit(visit.interventionPoint);

    let color;
    switch (targetStatus.status) {
      case 'under-target':
        color = 'text-red-600 bg-red-50';
        break;
      case 'over-target':
        color = 'text-orange-600 bg-orange-50';
        break;
      case 'on-target':
        color = 'text-green-600 bg-green-50';
        break;
      case 'no-target':
        color = 'text-yellow-600 bg-yellow-50';
        break;
      default:
        color = 'text-gray-500 bg-gray-50';
    }

    return {
      ...targetStatus,
      color
    };
  };

    // Fonction pour calculer le statut d'avancement annuel style Strava
  const getAnnualTargetProgress = (visit) => {
    if (!visit.interventionPoint) {
      return {
        hasTarget: false,
        color: 'text-gray-500',
        message: 'Magasin non défini'
      };
    }

    // Vérifier si les données sont disponibles
    if (!filteredTargets || filteredTargets.length === 0 || annualProgressLoading) {
      return {
        hasTarget: false,
        color: 'text-gray-500',
        message: 'Chargement...'
      };
    }

    // Trouver l'objectif pour ce magasin
    const target = filteredTargets.find(t => t.intervention_point_name === visit.interventionPoint);
    if (!target) {
      return {
        hasTarget: false,
        color: 'text-gray-500',
        message: 'Pas d\'objectif'
      };
    }

    const now = new Date();
    const currentYear = now.getFullYear();
    const yearStart = startOfYear(new Date(currentYear, 0, 1));
    const yearEnd = endOfYear(new Date(currentYear, 0, 1));

    // Jours écoulés depuis le début de l'année
    const daysPassed = differenceInDays(now, yearStart) + 1;
    const totalDaysInYear = differenceInDays(yearEnd, yearStart) + 1;

    // Utiliser les données du nouvel endpoint si disponibles
    let completedVisitsCount = 0;
    if (annualProgressData && annualProgressData.yearToDateCounts) {
      completedVisitsCount = annualProgressData.yearToDateCounts[visit.interventionPoint] || 0;
    } else {
      // Fallback sur les données existantes. countStoreVisits, pas .length : la table
      // est grainée par (visite × univers) et l'objectif est par magasin — voir
      // l'endpoint annual-progress, qui déduplique pour la même raison.
      completedVisitsCount = countStoreVisits(visits.filter(v =>
        isCompletedVisit(v) && v.interventionPoint === visit.interventionPoint
      ));
    }

    // Compter la visite planifiée actuelle
    const totalVisitsIncludingPlanned = completedVisitsCount + 1;

    const annualTarget = target.visit_frequency;
    const expectedVisitsToDate = (annualTarget * daysPassed) / totalDaysInYear;
    const remainingVisits = Math.max(0, annualTarget - totalVisitsIncludingPlanned);

    // Calculer l'avance/retard
    const progressDifference = totalVisitsIncludingPlanned - expectedVisitsToDate;
    const progressPercentage = (totalVisitsIncludingPlanned / annualTarget) * 100;

    // Déterminer le statut et la couleur
    let status, color, message;
    if (progressDifference >= 1) {
      status = 'ahead';
      color = 'text-green-600 bg-green-50';
      message = `En avance de ${Math.round(progressDifference)} visite${Math.round(progressDifference) > 1 ? 's' : ''}`;
    } else if (progressDifference <= -1) {
      status = 'behind';
      color = 'text-red-600 bg-red-50';
      message = `En retard de ${Math.round(Math.abs(progressDifference))} visite${Math.round(Math.abs(progressDifference)) > 1 ? 's' : ''}`;
    } else {
      status = 'on-track';
      color = 'text-green-600 bg-green-50';
      message = 'Dans les temps';
    }

    return {
      hasTarget: true,
      status,
      color,
      message,
      remainingVisits,
      totalVisitsIncludingPlanned,
      annualTarget,
      progressPercentage: Math.round(progressPercentage),
      expectedVisitsToDate: Math.round(expectedVisitsToDate * 10) / 10,
      progressDifference: Math.round(progressDifference * 10) / 10
    };
  };

    // Colonnes de base
  const baseColumns = [
    {
      key: 'shopSign',
      label: 'Enseigne',
      render: (visit) => visit.shopSign || '—'
    },
    {
      key: 'interventionPoint',
      label: 'Magasin',
      render: (visit) => visit.interventionPoint || '—'
    },
    {
      key: 'targetStatus',
      label: 'Objectif Avant Visite',
      render: (visit) => {
        const targetStatus = getVisitTargetStatus(visit);
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${targetStatus.color}`}>
            {targetStatus.message}
          </span>
        );
      },
      rawValue: (visit) => {
        const targetStatus = getVisitTargetStatus(visit);
        return targetStatus.message;
      },
      sortFunction: (a, b, direction) => {
        const aStatus = getVisitTargetStatus(a);
        const bStatus = getVisitTargetStatus(b);
        const statusOrder = {
          'under-target': 1,
          'over-target': 2,
          'on-target': 3,
          'no-target': 4,
          'no-data': 5
        };
        const aOrder = statusOrder[aStatus.status] || 5;
        const bOrder = statusOrder[bStatus.status] || 5;
        return direction === 'ascending' ? aOrder - bOrder : bOrder - aOrder;
      }
    },
    {
      key: 'targetStatusAfterVisit',
      label: 'Objectif Après Visite',
      render: (visit) => {
        const currentStatus = getVisitTargetStatus(visit);
        const afterStatus = getVisitTargetStatusAfterPlanned(visit);

        // Si pas d'objectif, afficher le message standard
        if (currentStatus.status === 'no-target' || currentStatus.status === 'no-data') {
          return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${afterStatus.color}`}>
              {afterStatus.message}
            </span>
          );
        }

        // Afficher le statut après visite avec détails
        const details = `${afterStatus.visitsAfterPlanned}/${afterStatus.expectedVisitsInPeriod} visites`;
        return (
          <div className="flex flex-col space-y-1">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${afterStatus.color}`}>
              {afterStatus.message}
            </span>
            <span className="text-xs text-gray-500" title={details}>
              {details}
            </span>
          </div>
        );
      },
      rawValue: (visit) => {
        const afterStatus = getVisitTargetStatusAfterPlanned(visit);
        return afterStatus.message;
      },
      sortFunction: (a, b, direction) => {
        const aStatus = getVisitTargetStatusAfterPlanned(a);
        const bStatus = getVisitTargetStatusAfterPlanned(b);
        const statusOrder = {
          'under-target': 1,
          'over-target': 2,
          'on-target': 3,
          'no-target': 4,
          'no-data': 5
        };
        const aOrder = statusOrder[aStatus.status] || 5;
        const bOrder = statusOrder[bStatus.status] || 5;
        return direction === 'ascending' ? aOrder - bOrder : bOrder - aOrder;
      }
    }
  ];

  // Colonne d'avancement annuel (uniquement si période >= 365 jours)
  const annualProgressColumn = {
    key: 'annualProgress',
    label: 'Avancement Objectif Annuel',
    render: (visit) => {
      const progressData = getAnnualTargetProgress(visit);

      if (!progressData.hasTarget) {
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium text-gray-500 bg-gray-50">
            {progressData.message}
          </span>
        );
      }

      // Déterminer la couleur de la barre selon le statut avec tolérance de ±1 visite
      let barColor;
      const difference = progressData.totalVisitsIncludingPlanned - progressData.expectedVisitsToDate;

      if (difference <= -1) {
        barColor = 'bg-red-400'; // En retard de plus d'1 visite (rouge)
      } else if (difference >= 1) {
        barColor = 'bg-orange-400'; // En avance de plus d'1 visite (orange)
      } else {
        barColor = 'bg-green-400'; // Dans la tolérance ±1 visite (vert)
      }

      return (
        <AnnualProgressBar
          progressData={progressData}
          showLegend={true}
          size="normal"
        />
      );
    },
    rawValue: (visit) => {
      const progressData = getAnnualTargetProgress(visit);
      return progressData.hasTarget ? progressData.remainingVisits : 999;
    },
    sortFunction: (a, b, direction) => {
      const aProgress = getAnnualTargetProgress(a);
      const bProgress = getAnnualTargetProgress(b);
      const aValue = aProgress.hasTarget ? aProgress.progressDifference : -999;
      const bValue = bProgress.hasTarget ? bProgress.progressDifference : -999;
      return direction === 'ascending' ? bValue - aValue : aValue - bValue; // Inversé pour avoir les en retard en premier
    }
  };

  // Colonnes finales
  const finalColumns = [
    {
      key: 'plannedStart',
      label: 'Date planifiée',
      render: (visit) => formatDateTime(visit.plannedStart),
      sortFunction: (a, b, direction) => {
        const aDate = a.plannedStart;
        const bDate = b.plannedStart;
        if (!aDate && !bDate) return 0;
        if (!aDate) return direction === 'ascending' ? 1 : -1;
        if (!bDate) return direction === 'ascending' ? -1 : 1;
        return direction === 'ascending' ? aDate - bDate : bDate - aDate;
      }
    },
    {
      key: 'username',
      label: 'Force de vente',
      render: (visit) => obfuscate ? obfuscateUsername(visit.username) : visit.username
    }
  ];

  // Calculer la période sélectionnée par l'utilisateur (non ajustée)
  const userSelectedPeriodDays = useMemo(() => {
    if (!startDate || !endDate) return 0;
    return differenceInDays(endDate, startDate) + 1;
  }, [startDate, endDate]);

  // Construire le tableau de colonnes
  const columns = useMemo(() => {
    const cols = [...baseColumns];

    // Debug pour vérifier les valeurs
    console.log('VisitsPlanned - userSelectedPeriodDays:', userSelectedPeriodDays, 'periodDays (hook):', periodDays, 'loading:', loading, 'annualProgressLoading:', annualProgressLoading, 'filteredTargets length:', filteredTargets?.length);

    // Ajouter la colonne d'avancement annuel si les données sont chargées
    if (!loading && !annualProgressLoading) {
      cols.push(annualProgressColumn);
    }

    cols.push(...finalColumns);
    return cols;
  }, [loading, annualProgressLoading, filteredTargets, annualProgressData]);

  return (
    <div className="space-y-4">
      <SortableTable
        data={plannedVisits}
        columns={columns}
        emptyMessage={endDate && endDate < new Date() ? "Pas de visite planifiée sur une période passée" : "Aucune visite planifiée à venir"}
        countLabel={`Total: ${plannedVisits.length} visite${plannedVisits.length !== 1 ? 's' : ''}`}
        initialSortBy={{ key: 'plannedStart', direction: 'ascending' }}
        exportable={true}
        exportFilename="visites_planifiees.csv"
      />
    </div>
  );
}

export default VisitsPlanned;


